import styled, { css } from 'styled-components';
import { theme } from '../../styles/theme';
import { IStyled } from '../../styles/types';
import { SvgIcon } from '../Icon';

/**
 * Props for the SwitchiOS component.
 * @interface IProps
 * @extends {IStyled}
 */
interface IProps extends IStyled {
  /**
   * Determines whether the switch is in the "on" (true) or "off" (false) state.
   */
  isChecked: boolean;

  /**
   * Callback function triggered when the switch is clicked.
   * @param {React.MouseEvent<HTMLSpanElement>} e - The click event.
   */
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;

  /**
   * When true, the switch appears dimmed and doesn't respond to clicks.
   * @default false
   */
  isDisabled?: boolean;

  /**
   * When true, renders a smaller version of the switch.
   * @default false
   */
  compact?: boolean;
}

const StyledSwitch = styled.span<{
  $isChecked: boolean;
  $compact?: boolean;
  $isDisabled?: boolean;
}>`
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border-radius: 25px;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  background-color: ${({ $isChecked }) =>
    $isChecked ? theme.colors.mainBlue : theme.colors.secondary800};

  // Disabled
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      cursor: not-allowed;
      opacity: 0.4;
    `}

  // Regular 
  width: 40px;
  height: 20px;

  // Compact
  ${({ $compact }) =>
    $compact &&
    css`
      width: 30px;
      height: 16px;
    `}
`;

const StyledKnob = styled.span<{
  $isChecked: boolean;
  $isCompact?: boolean;
  $isDisabled?: boolean;
}>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: 0.2s;
  will-change: transform;
  background-color: ${({ $isDisabled }) =>
    $isDisabled ? theme.colors.secondary400 : theme.colors.white};

  // Regular
  width: 16px;
  height: 16px;
  transform: translateX(${({ $isChecked }) => ($isChecked ? '20px' : '0')});

  // Compact
  ${({ $isCompact, $isChecked }) =>
    $isCompact &&
    css`
      width: 12px;
      height: 12px;
      transform: translateX(${$isChecked ? '14px' : '0'});
    `}
`;

const SwitchiOS = ({
  isChecked,
  onClick,
  isDisabled = false,
  className,
  compact,
}: IProps) => (
  <StyledSwitch
    onClick={onClick}
    className={className}
    $isDisabled={isDisabled}
    $isChecked={isChecked}
    $compact={compact}
  >
    <StyledKnob
      $isChecked={isChecked}
      $isCompact={compact}
      $isDisabled={isDisabled}
    >
      {isChecked && !compact && (
        <SvgIcon
          name="check"
          width={16}
          height={16}
          customFill={theme.colors.secondary800}
        />
      )}
    </StyledKnob>
  </StyledSwitch>
);

export default SwitchiOS;
